/**
 * La copertura della commessa, dal lato della pagina.
 *
 * **Le letture stanno qui, le scritture no.** Che cosa manca, quali fotogrammi
 * restano da guardare, con quale vocabolario si dichiara: sono domande. Le tre
 * dichiarazioni — fotogramma guardato, buco spiegato, anomalia trovata — passano
 * dalla casella d'uscita degli intenti (GI-172), perche' una decisione che
 * sopravvive alla rete e' un oggetto e non una chiamata. Una seconda strada
 * diretta da qui sarebbe la strada che scrive **mentre** la prima aspetta: la
 * stessa dichiarazione due volte, o nessuna delle due.
 *
 * **I filtri stanno nell'indirizzo, non in memoria.** Un turno di copertura non
 * e' una sessione: si guarda un pezzo di linea, si passa la parola a un
 * collega, si riprende domani. Filtri che vivono nello stato di React muoiono
 * col ricaricamento, e cio' che l'operatore aveva davanti non si puo' ne'
 * incollare in una segnalazione ne' ritrovare.
 */
import { get } from './api'
import { tx } from './i18n/t'
import type {
  Box,
  CoverageMap,
  Code,
  Frame,
  FrameFindings,
  FrameStrip,
  Frames,
  NotInspectedReason,
} from './types'

/**
 * Lo stato che l'indirizzo porta, per un solo modulo.
 *
 * Un tipo e non una borsa di stringhe: la schermata legge `reviewed` come tre
 * casi — solo revisionati, solo non revisionati, tutti — e leggerlo da una
 * stringa in ogni punto in cui serve significa sbagliarne uno.
 */
export interface CoverageRouteState {
  /** `null` e' «tutti»: l'assenza di filtro non e' un filtro. */
  reviewed: boolean | null
  noCandidates: boolean | null
  element: string | null
  camera: string | null
  /** Il massimo `gsd_mm` ammesso: piu' basso, piu' fine il dettaglio. */
  quality: number | null
  limit: number
  offset: number
  /** Il fotogramma scelto: la selezione e' stato di indirizzo come i filtri. */
  frame: string | null
  /** Parte del nome del file, per chi ricorda «RETRO» o l'ora dello scatto. */
  q?: string | null
}

/** Quanti per pagina, come il servizio. Ripetuto perche' il primo disegno non
 *  deve aspettare una risposta per sapere quanto chiedere. */
export const PAGE = 60
export const MAX_PAGE = 200

export const EMPTY: CoverageRouteState = {
  reviewed: null,
  noCandidates: null,
  element: null,
  camera: null,
  quality: null,
  limit: PAGE,
  offset: 0,
  frame: null,
}

/** I filtri della ricerca, tradotti per il servizio. La selezione e la mappa
 *  non ci entrano: quelle riguardano cio' che si guarda, non cio' che si
 *  chiede. */
function frameQuery(state: CoverageRouteState): string {
  const query = new URLSearchParams()
  if (state.reviewed !== null) query.set('reviewed', String(state.reviewed))
  if (state.noCandidates !== null) query.set('no_candidates', String(state.noCandidates))
  if (state.element !== null) query.set('element', state.element)
  if (state.camera !== null) query.set('camera', state.camera)
  if (state.quality !== null) query.set('quality', String(state.quality))
  if (state.q) query.set('q', state.q)
  query.set('limit', String(state.limit))
  query.set('offset', String(state.offset))
  return query.toString()
}

export function coverageMap(
  inspectionId: string,
  options: { element?: string | null; status?: string | null; limit?: number; offset?: number } = {},
): Promise<CoverageMap> {
  const query = new URLSearchParams()
  if (options.element) query.set('element', options.element)
  if (options.status) query.set('status', options.status)
  query.set('limit', String(options.limit ?? MAX_PAGE))
  query.set('offset', String(options.offset ?? 0))
  return get<CoverageMap>(
    `/inspections/${encodeURIComponent(inspectionId)}/coverage-map?${query.toString()}`,
  )
}

export function frames(inspectionId: string, state: CoverageRouteState): Promise<Frames> {
  return get<Frames>(
    `/inspections/${encodeURIComponent(inspectionId)}/search/frames?${frameQuery(state)}`,
  )
}

/** Quanti fotogrammi per lato chiede il nastro del banco. Come il servizio:
 *  abbastanza per la campata prima e quella dopo, pochi da non pesare sul
 *  cambio di candidato. */
export const STRIP_RADIUS = 24

/** I fotogrammi attorno a uno, della stessa camera, in ordine di volo (GI-265). */
export function frameStrip(
  inspectionId: string,
  photoId: string,
  radius = STRIP_RADIUS,
): Promise<FrameStrip> {
  return get<FrameStrip>(
    `/inspections/${encodeURIComponent(inspectionId)}/search/frames/${encodeURIComponent(photoId)}/strip?radius=${radius}`,
  )
}

/** Le anomalie di un fotogramma, con riquadro e verdetto: per disegnarle. */
export function frameFindings(inspectionId: string, photoId: string): Promise<FrameFindings> {
  return get<FrameFindings>(
    `/inspections/${encodeURIComponent(inspectionId)}/search/frames/${encodeURIComponent(photoId)}/findings`,
  )
}

export function catalogFor(inspectionId: string, elementId: string): Promise<Code[]> {
  return get<Code[]>(
    `/inspections/${encodeURIComponent(inspectionId)}/search/catalog` +
      `?element_id=${encodeURIComponent(elementId)}`,
  )
}

export function notInspectedReasons(inspectionId: string): Promise<NotInspectedReason[]> {
  return get<NotInspectedReason[]>(
    `/inspections/${encodeURIComponent(inspectionId)}/not-inspected-reasons`,
  )
}

/** Il riquadro come quattro campi di testo: cio' che l'operatore ha battuto. */
export interface BoxDraft {
  x: string
  y: string
  width: string
  height: string
}

export const NO_BOX: BoxDraft = { x: '', y: '', width: '', height: '' }

const CORNERS = ['x', 'y', 'width', 'height'] as const

function fieldLabel(name: (typeof CORNERS)[number]): string {
  if (name === 'width') return tx('box.fieldWidth')
  if (name === 'height') return tx('box.fieldHeight')
  return name === 'x' ? 'X' : 'Y'
}

/** Il riquadro tracciato, riportato nei quattro campi. */
export function draftOf(box: Box): BoxDraft {
  return {
    x: String(box.x),
    y: String(box.y),
    width: String(box.width),
    height: String(box.height),
  }
}

/**
 * Il riquadro, se i quattro campi lo descrivono davvero.
 *
 * **Sta qui e non in una schermata** perche' due schermate lo compongono — la
 * copertina in Carta con i quattro campi, la ricerca libera in Cockpit col
 * trascinamento — e due convalide della stessa cosa divergono al primo caso
 * limite corretto in una sola delle due.
 *
 * Il messaggio nomina il campo: «riquadro non valido» manda a guardarne quattro
 * per trovare quello sbagliato. Il servizio ricontrolla tutto: questa convalida
 * serve a non far viaggiare un rifiuto annunciato, non a sostituirla.
 */
/** I livelli che il codice in vigore ammette. Vuoto nel catalogo non e' «nessuno»:
 *  e' «il catalogo non li elenca», e restano 1, 2 e 3. */
export function levelsOf(codes: Code[], code: string): number[] {
  const listed = codes.find((one) => one.code === code)?.levels ?? []
  return listed.length > 0 ? listed : [1, 2, 3]
}

export function boxOf(draft: BoxDraft, frame: Frame | null): { box: Box } | { error: string } {
  const numbers: Record<string, number> = {}
  for (const name of CORNERS) {
    const raw = draft[name].trim()
    if (raw === '') return { error: tx('box.missing', { field: fieldLabel(name) }) }
    const value = Number(raw)
    if (!Number.isInteger(value)) {
      return { error: tx('box.notInteger', { field: fieldLabel(name) }) }
    }
    numbers[name] = value
  }
  const box: Box = {
    x: numbers.x ?? 0,
    y: numbers.y ?? 0,
    width: numbers.width ?? 0,
    height: numbers.height ?? 0,
  }
  if (box.x < 0 || box.y < 0) return { error: tx('box.outside') }
  if (box.width <= 0 || box.height <= 0) {
    return { error: tx('box.noArea') }
  }
  const width = frame?.width ?? null
  const height = frame?.height ?? null
  if (width !== null && box.x + box.width > width) {
    return { error: tx('box.overflowWidth', { n: width }) }
  }
  if (height !== null && box.y + box.height > height) {
    return { error: tx('box.overflowHeight', { n: height }) }
  }
  return { box }
}
